import React from 'react';
import {
  FiChevronDown,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiTarget,
  FiShare2,
} from 'react-icons/fi';
import { Pipeline, Fund } from '../../types/fundraise';
import KanbanBoard from './KanbanBoard';
import { formatCurrency } from '../../utils/helpers';

// Type cast icons for React 18 compatibility
const ChevronDownIcon = FiChevronDown as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit2 as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const TargetIcon = FiTarget as React.FC<React.SVGProps<SVGSVGElement>>;
const ShareIcon = FiShare2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface PipelineSectionProps {
  pipeline: Pipeline;
  funds: Fund[];
  onToggleExpand: (pipelineId: string, isExpanded: boolean) => void;
  onEdit: (pipeline: Pipeline) => void;
  // onDelete is optional — undefined for shared pipelines where the viewer
  // is only a collaborator and cannot destroy the pipeline.
  onDelete?: (pipeline: Pipeline) => void;
  onAddFund: (pipeline: Pipeline) => void;
  onMoveFund: (fundId: string, newStage: string, position: number) => void;
  // onDeleteFund undefined → trash button is hidden on cards (FundCard
  // already conditionally renders it based on this prop).
  onDeleteFund?: (fund: Fund) => void;
  onFundClick?: (fund: Fund) => void;
  onShare?: (pipeline: Pipeline) => void;
}

const PipelineSection: React.FC<PipelineSectionProps> = ({
  pipeline,
  funds,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddFund,
  onMoveFund,
  onDeleteFund,
  onFundClick,
  onShare,
}) => {
  const fundCount = funds.length;
  const stages = pipeline.stages || [];

  // Calculate progress based on stage completion
  // Progress shows how many stages have funds in them
  const stagesWithFunds = stages.filter((stage) =>
    funds.some((f) => f.stage === stage.id)
  ).length;
  const progressPercentage = stages.length > 0 ? stagesWithFunds / stages.length : 0;

  // Generate progress dots (3 dots showing progress)
  const getProgressDots = () => {
    const dots = [];
    const filledDots = Math.floor(progressPercentage * 3);

    for (let i = 0; i < 3; i++) {
      const className = i < filledDots
        ? 'su-pipeline-progress-dot filled'
        : 'su-pipeline-progress-dot empty';
      dots.push(<span key={i} className={className} />);
    }
    return dots;
  };

  const handleHeaderClick = () => {
    onToggleExpand(pipeline.id, !pipeline.is_expanded);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(pipeline);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) onDelete(pipeline);
  };

  const handleAddFundClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddFund(pipeline);
  };

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleHeaderClick();
    }
  };

  return (
    <div className={`su-pipeline-section ${pipeline.is_expanded ? 'expanded' : ''}`}>
      <div
        className="su-pipeline-header"
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={pipeline.is_expanded}
      >
        <div className="su-pipeline-header-left">
          <ChevronDownIcon className="su-pipeline-chevron" width={20} height={20} />
          <div className="su-pipeline-icon">
            <TargetIcon width={16} height={16} />
          </div>
          <span className="su-pipeline-name">{pipeline.name}</span>
          {pipeline.is_collaborator && (
            <span
              className="su-fundraise-shared-badge"
              title={
                pipeline.owner_email
                  ? `Shared by ${pipeline.owner_name || pipeline.owner_email}`
                  : 'Shared with you'
              }
            >
              Shared{pipeline.owner_name ? ` by ${pipeline.owner_name}` : pipeline.owner_email ? ` by ${pipeline.owner_email}` : ''}
            </span>
          )}
          <span className="su-pipeline-target">
            Target: {formatCurrency(pipeline.target_amount)}
          </span>
          <span className="su-pipeline-fund-count">
            {fundCount} {fundCount === 1 ? 'fund' : 'funds'}
          </span>
          <div className="su-pipeline-progress">{getProgressDots()}</div>
        </div>
        <div className="su-pipeline-header-right">
          <button
            type="button"
            className="su-pipeline-add-fund-btn"
            onClick={handleAddFundClick}
            aria-label="Add fund to pipeline"
          >
            <PlusIcon width={16} height={16} />
            ADD FUND
          </button>
          {onShare && (
            <button
              type="button"
              className="su-pipeline-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onShare(pipeline);
              }}
              title="Share pipeline"
              aria-label="Share pipeline"
            >
              <ShareIcon width={16} height={16} />
            </button>
          )}
          <button
            type="button"
            className="su-pipeline-action-btn"
            onClick={handleEditClick}
            title="Edit pipeline"
            aria-label="Edit pipeline"
          >
            <EditIcon width={16} height={16} />
          </button>
          {onDelete && (
            <button
              type="button"
              className="su-pipeline-action-btn delete"
              onClick={handleDeleteClick}
              title="Delete pipeline"
              aria-label="Delete pipeline"
            >
              <TrashIcon width={16} height={16} />
            </button>
          )}
        </div>
      </div>
      <div className="su-pipeline-content">
        {pipeline.is_expanded && (
          <KanbanBoard
            stages={stages}
            funds={funds}
            onMoveFund={onMoveFund}
            onDeleteFund={onDeleteFund}
            onFundClick={onFundClick}
          />
        )}
      </div>
    </div>
  );
};

export default PipelineSection;
